export const getConversationType = (conversation) => (conversation?.conversation_type || 'invite');

export const getConversationId = (conversation) => {
  if (!conversation) return null;
  const type = getConversationType(conversation);
  if (type === 'application') {
    return conversation.campaign_application_id || conversation.id || conversation.campaign_influencer_id;
  }
  return conversation.campaign_influencer_id || conversation.id;
};

export const getConversationIdentifiers = (conversation) => {
  const type = getConversationType(conversation);
  const id = getConversationId(conversation);
  const key = id ? `${type}-${id}` : null;
  return { type, id, key };
};

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

export const getChatMode = (conversation) => {
  const explicitMode = normalizeStatus(conversation?.chat_mode);
  if (['active', 'negotiate', 'closed'].includes(explicitMode)) return explicitMode;

  const status = normalizeStatus(conversation?.status);
  const acceptanceStatus = normalizeStatus(conversation?.acceptance_status);

  if (['completed', 'cancelled', 'canceled'].includes(acceptanceStatus)) return 'closed';
  if (['rejected', 'expired', 'cancelled', 'canceled'].includes(status)) return 'closed';
  if (['pending', 'negotiating'].includes(status)) return 'negotiate';
  if (['accepted', 'approved'].includes(status)) return 'active';
  return 'closed';
};

/** Labels aligned with the Active / Negotiating / Archived inbox chips. */
export const getChatModeLabel = (modeOrConversation) => {
  const mode = typeof modeOrConversation === 'string'
    ? normalizeStatus(modeOrConversation)
    : getChatMode(modeOrConversation);
  if (mode === 'active') return 'Active';
  if (mode === 'negotiate') return 'Negotiating';
  return 'Archived';
};

export const filterConversationsByType = (conversations, filter) => {
  if (!conversations) return [];
  if (filter === 'all') return conversations;
  return conversations.filter((c) => {
    const chatMode = getChatMode(c);
    if (filter === 'active') return chatMode === 'active';
    if (filter === 'archived') return chatMode === 'closed';
    if (filter === 'negotiate') return chatMode === 'negotiate';

    const convType = c.conversation_type || 'invite';
    if (filter === 'invites') return convType === 'invite';
    if (filter === 'applications') return convType === 'application';
    return true;
  });
};

export const sortConversationsByRecency = (conversations) => {
  if (!conversations?.length) return [];
  return [...conversations].sort((a, b) => {
    const aTime = a.last_message_at
      ? new Date(a.last_message_at).getTime()
      : new Date(a.created_at || 0).getTime();
    const bTime = b.last_message_at
      ? new Date(b.last_message_at).getTime()
      : new Date(b.created_at || 0).getTime();
    return bTime - aTime;
  });
};

export const getLastReadMs = (lastReadByConv, key) => {
  const v = lastReadByConv?.[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
};

/** Merge the latest known activity for a conversation into the read cursor. */
export const mergeReadThroughMs = (prevMs, conv) => {
  if (conv?.last_message_at) {
    const t = new Date(conv.last_message_at).getTime();
    if (!Number.isNaN(t)) return Math.max(prevMs || 0, t);
  }
  return Math.max(prevMs || 0, Date.now());
};

/**
 * Unread = last activity in the thread is newer than the local read cursor, sent
 * by the other party, and the thread is not currently in the foreground.
 */
export const isUnreadIncomingFromOtherParty = ({
  conv,
  conversationKey,
  lastReadByConv,
  userType,
  activeConvKey,
  isOpen,
}) => {
  if (!conv?.last_message_at || !conversationKey) return false;
  const lastMessageTime = new Date(conv.last_message_at).getTime();
  if (Number.isNaN(lastMessageTime)) return false;
  if (lastMessageTime <= getLastReadMs(lastReadByConv, conversationKey)) return false;

  const isForeground = activeConvKey && conversationKey === activeConvKey && isOpen;
  if (isForeground) return false;

  const st = (conv.last_message_sender_type || '').toLowerCase();
  const ut = (userType || '').toLowerCase();
  if (ut === 'influencer' && st === 'brand') return true;
  if (ut === 'brand' && st === 'influencer') return true;
  return false;
};

/**
 * Return a new conversations array with the conversation matching the SSE
 * payload patched with the latest message metadata. Returns the same array
 * reference (and `matched: false`) when no conversation matches so callers can
 * decide whether a full refetch is needed to discover a new conversation.
 */
export const patchConversationsWithMessage = (conversations, { conversationId, conversationType, message }) => {
  if (!Array.isArray(conversations) || !conversations.length || !message) {
    return { conversations, matched: false };
  }

  let matched = false;
  const next = conversations.map((conv) => {
    const type = getConversationType(conv);
    const id = getConversationId(conv);
    if (String(id) === String(conversationId) && type === conversationType) {
      matched = true;
      return {
        ...conv,
        last_message_at: message.created_at || conv.last_message_at,
        last_message_sender_type: message.sender_type || conv.last_message_sender_type,
      };
    }
    return conv;
  });

  return { conversations: matched ? next : conversations, matched };
};
